/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { Flex } from '#UNSAFE_Flex';
import { PaddingProps, paddingInterpolations } from '#utils/UNSAFE_interpolations/padding';
import { mergeInterpolations } from '#utils/UNSAFE_mergeInterpolations';
import { ComponentChildren, ComponentProps } from 'preact';
import { containerStyles } from './DatePickerLayoutStyles.css';
import { TestIdProps, useTestId } from '#hooks/UNSAFE_useTestId';

type FlexProps = ComponentProps<typeof Flex>;

type Props = TestIdProps & {
  /**
   * Use this property to provide rendered content for the
   * header region.
   */
  header?: ComponentChildren;

  /**
   * Use this property to provide rendered content for the
   * main region.
   */
  children?: ComponentChildren;

  /**
   * Use this property to specify the spacing between the header and the main content.
   * @default '4x'
   */
  gap?: FlexProps['gap'];

  /**
   * Use this property to specify the spacing around the content.
   * @default '4x'
   */
  padding?: PaddingProps['padding'];

  /**
   * Use this property to specify the spacing between bottom edge and the main content.
   * @default '4x'
   */
  paddingBlockEnd?: PaddingProps['paddingBlockEnd'];

  /**
   * Use this property to specify the spacing between top edge and the header content.
   * @default '4x'
   */
  paddingBlockStart?: PaddingProps['paddingBlockStart'];

  /**
   * Use this property to specify the spacing between right edge and the content.
   * @default '4x'
   */
  paddingInlineEnd?: PaddingProps['paddingInlineEnd'];

  /**
   * Use this property to specify the spacing between left edge and the content.
   * @default '4x'
   */
  paddingInlineStart?: PaddingProps['paddingInlineStart'];
};

const interpolations = [...Object.values(paddingInterpolations)];
const mergedInterpolations = mergeInterpolations<Props>(interpolations);

/**
 * The DatePickerLayout component can be used to layout the contents
 * of the DatePicker, like header and the main content.
 */
export const DatePickerLayout = ({
  children,
  gap = '4x',
  header,
  padding,
  paddingBlockEnd = '4x',
  paddingBlockStart = '4x',
  paddingInlineEnd = '4x',
  paddingInlineStart = '4x',
  testId
}: Props) => {
  const styles = mergedInterpolations({
    padding,
    paddingBlockEnd,
    paddingBlockStart,
    paddingInlineEnd,
    paddingInlineStart
  });
  const testIdProps = useTestId(testId);

  return (
    <div className={containerStyles} style={styles} {...testIdProps}>
      <Flex direction="column" gap={gap}>
        {header}
        {children}
      </Flex>
    </div>
  );
};
